import { END_POINTS } from "../constants/urls";
import emptySplitApi from "../utils/rtk";

type CommonResponse = {
  status: number;
  data?: any;
  message: string;
};

export const userApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getUsers: builder.query<
      CommonResponse,
      {
        search: string;
        status: string;
        page: number;
        body: any;
        sortOrder: string;
      }
    >({
      query: ({ search, status, page, body, sortOrder }) => ({
        url: `${END_POINTS.getUser}?search=${search}&status=${status}&pageNumber=${page}&sortOrder=${sortOrder}`,
        method: "POST",
        body,
      }),
    }),

    cancelMember: builder.mutation<CommonResponse, any>({
      query: (body) => ({
        url: END_POINTS.cancelMember,
        method: "POST",
        body,
      }),
    }),

    approveMember: builder.mutation<CommonResponse, any>({
      query: (body) => ({
        url: END_POINTS.approveMember,
        method: "PATCH",
        body,
      }),
    }),

    addUser: builder.mutation<CommonResponse, any>({
      query: (body) => ({
        url: END_POINTS.addUser,
        method: "POST",
        body,
      }),
    }),

    updateUser: builder.mutation<CommonResponse, any>({
      query: (body) => ({
        url: END_POINTS.editUser,
        method: "PATCH",
        body,
      }),
    }),

    memberDetails: builder.query<
      CommonResponse,
      { userId: string | undefined }
    >({
      query: ({ userId }) => ({
        url: `${END_POINTS.userDetail}/${userId}`,
        method: "GET",
      }),
    }),

    getMemberGroups: builder.query<
      CommonResponse,
      { search: string; page: number }
    >({
      query: ({ search, page }) => ({
        url: `${END_POINTS.getMemberGroups}?search=${search}`,
        method: "GET",
      }),
    }),

    addMemberGroup: builder.mutation<CommonResponse, { groupName: string }>({
      query: ({ groupName }) => ({
        url: `${END_POINTS.addMemberGroups}?GroupName=${groupName}`,
        method: "POST",
      }),
    }),

    deleteMemberGroup: builder.query<CommonResponse, { id: string }>({
      query: ({ id }) => ({
        url: `${END_POINTS.deleteMemberGroup}/${id}`,
        method: "DELETE",
      }),
    }),

    makeMemberVip: builder.mutation<CommonResponse, { id: string | undefined }>(
      {
        query: ({ id }) => ({
          url: `${END_POINTS.makeVip}/${id}`,
          method: "PATCH",
        }),
      }
    ),

    allMembers: builder.query<CommonResponse, { search: string }>({
      query: ({ search }) => ({
        url: `${END_POINTS.allMembers}?search=${search}`,
        method: "GET",
      }),
    }),

    allMembersByGroup: builder.query<
      CommonResponse,
      { search: string; id: string | undefined }
    >({
      query: ({ search, id }) => ({
        url: `${END_POINTS.getMembersByGroup}/${id}?search=${search}`,
        method: "GET",
      }),
    }),

    addMemberToGroup: builder.mutation<CommonResponse, any>({
      query: (body) => ({
        url: `${END_POINTS.addMemberToGroup}`,
        method: "POST",
        body,
      }),
    }),

    addMemberToMultipleGroup: builder.mutation<CommonResponse, any>({
      query: (body) => ({
        url: `${END_POINTS.addMemberToMultipleGroups}`,
        method: "POST",
        body,
      }),
    }),

    getGroupByMember: builder.query<CommonResponse, { id: string | undefined }>(
      {
        query: ({ id }) => ({
          url: `${END_POINTS.getUserByGroupId}/${id}`,
          method: "GET",
        }),
      }
    ),

    changeUserBio: builder.mutation<CommonResponse, any>({
      query: (body) => ({
        url: `${END_POINTS.changeBio}`,
        method: "PATCH",
        body,
      }),
    }),

    getPreviousQuestion: builder.query<CommonResponse, {}>({
      query: () => ({
        url: `${END_POINTS.previousQuestion}`,
        method: "GET",
      }),
    }),

    getUserBookingHistory: builder.query<
      CommonResponse,
      { page: number; userId: string | undefined; search: string }
    >({
      query: ({ page, userId, search }) => ({
        url: `${END_POINTS.userBookingHistory}?pageNumber=${page}&userId=${userId}&search=${search}`,
        method: "GET",
      }),
    }),

    getUserTransactionHistory: builder.query<
      CommonResponse,
      { page: number; userId: string | undefined; search: string }
    >({
      query: ({ page, userId, search }) => ({
        url: `${END_POINTS.userTransactionHistory}?pageNumber=${page}&userId=${userId}&search=${search}`,
        method: "GET",
      }),
    }),

    generateLink: builder.mutation<CommonResponse, any>({
      query: (body) => ({
        url: `${END_POINTS.generateLink}`,
        method: "POST",
        body,
      }),
    }),

    changeUserStatus: builder.query<
      CommonResponse,
      { userId: string | undefined }
    >({
      query: ({ userId }) => ({
        url: `${END_POINTS.changeUserStatus}?userId=${userId}`,
        method: "GET",
      }),
    }),

    cancelSubscription: builder.query<
      CommonResponse,
      { userId: string | undefined }
    >({
      query: ({ userId }) => ({
        url: `${END_POINTS.cancelSubscription}?userId=${userId}`,
        method: "GET",
      }),
    }),

    referHistory: builder.query<
      CommonResponse,
      { userId: string | undefined; search: string; page: number }
    >({
      query: ({ userId, search, page }) => ({
        url: `${END_POINTS.referHistory}?userId=${userId}&search=${search}&pageNumber=${page}`,
        method: "GET",
      }),
    }),

    deleteUser: builder.query<CommonResponse, { id: string }>({
      query: ({ id }) => ({
        url: `${END_POINTS.deleteUser}?userId=${id}`,
        method: "DELETE",
      }),
    }),

    exportUser: builder.query<
      CommonResponse,
      { format: string | undefined; status: string; search: string; body: any }
    >({
      query: ({ format, status, search, body }) => ({
        url: `${END_POINTS.exportUser}?format=${format}&status=${status}&search=${search}`,
        method: "POST",
        body,
      }),
    }),

    refundSubscription: builder.mutation<CommonResponse, any>({
      query: (body) => ({
        url: `${END_POINTS.refundSubscription}`,
        method: "POST",
        body,
      }),
    }),
  }),
});

export const {
  useLazyGetUsersQuery,
  useApproveMemberMutation,
  useCancelMemberMutation,
  useAddUserMutation,
  useLazyMemberDetailsQuery,
  useUpdateUserMutation,
  useLazyGetMemberGroupsQuery,
  useAddMemberGroupMutation,
  useLazyDeleteMemberGroupQuery,
  useMakeMemberVipMutation,
  useLazyAllMembersQuery,
  useLazyAllMembersByGroupQuery,
  useAddMemberToGroupMutation,
  useAddMemberToMultipleGroupMutation,
  useLazyGetGroupByMemberQuery,
  useChangeUserBioMutation,
  useLazyGetPreviousQuestionQuery,
  useLazyGetUserBookingHistoryQuery,
  useLazyGetUserTransactionHistoryQuery,
  useGenerateLinkMutation,
  useLazyChangeUserStatusQuery,
  useLazyCancelSubscriptionQuery,
  useLazyReferHistoryQuery,
  useLazyDeleteUserQuery,
  useLazyExportUserQuery,
  useRefundSubscriptionMutation,
} = userApi;
